import { logContact } from '../services/communicationLog';
import CallTalkingPoints from './CallTalkingPoints';
import React, { useState } from 'react';

// Lender quick actions for the financing/appraisal deadline and clear-to-close tasks.
// Call, Text and Email use the lender saved on the deal. Send marks the task done.
const isLenderTask = (name) => /^Financing \/ Appraisal Deadline/i.test(name) || /^Financing Contingency/i.test(name) || /^Financing Check-In/i.test(name) || /Clear To Close/i.test(name);

const messageFor = (name, first, address) => {
  if (/Clear To Close/i.test(name)) {
    return `Hi ${first || 'there'}, we're about three days from closing on ${address || 'the property'}. Do we have clear to close? Please let me know if anything is outstanding. - Kelly Olin, RESIDENCE | eXp Realty. Call or text me at 239-488-5900.`;
  }
  if (/^Financing Contingency/i.test(name)) {
    return `Hi ${first || 'there'}, the financing contingency deadline for ${address || 'the property'} is tomorrow. Please confirm the loan is on track and that no extension is needed. - Kelly Olin, RESIDENCE | eXp Realty. Call or text me at 239-488-5900.`;
  }
  return `Hi ${first || 'there'}, the financing and appraisal deadline for ${address || 'the property'} is tomorrow. Please confirm the loan is on track and that no extension is needed. - Kelly Olin, RESIDENCE | eXp Realty. Call or text me at 239-488-5900.`;
};

// Seller deals store the lender on the Under Contract form; fall back to those fields when no deal-level lender is saved.
export const lenderFromDeal = (deal) => {
  if (deal?.lender) return deal.lender;
  const uc = deal?.underContract || {};
  if (!uc.lenderFirstName && !uc.lenderLastName && !uc.lenderPhone && !uc.lenderEmail) return null;
  return { firstName: uc.lenderFirstName || '', lastName: uc.lenderLastName || '', phone: uc.lenderPhone || '', email: uc.lenderEmail || '', companyName: '' };
};

export default function LenderQuickContact({ task, lender, dealAddress, onSent }) {
  const taskName = task.name || task.title || '';
  const [open, setOpen] = useState(null);
  const [text, setText] = useState('');
  const [subject, setSubject] = useState('');

  if (!isLenderTask(taskName)) return null;

  const first = lender?.firstName || '';
  const name = `${lender?.firstName || ''} ${lender?.lastName || ''}`.trim();
  const company = lender?.companyName || '';
  const digits = (lender?.phone || '').replace(/[^0-9+]/g, '');
  const email = lender?.email || '';

  const btn = { display: 'inline-block', padding: '8px 14px', borderRadius: 6, fontWeight: 700, fontSize: 13, textDecoration: 'none', border: '1px solid #B8962E', background: '#B8962E', color: '#FFFFFF', cursor: 'pointer' };
  const btnOff = { ...btn, background: '#FFFFFF', color: '#B8962E', borderColor: '#D5D9E0', pointerEvents: 'none', opacity: 0.5 };
  const btnCancel = { ...btn, background: '#FFFFFF', color: '#5B6270', borderColor: '#D5D9E0' };

  const toggle = (mode) => {
    if (open === mode) { setOpen(null); return; }
    if (mode === 'text') setText(messageFor(taskName, first, dealAddress));
    if (mode === 'email') {
      setSubject(/Clear To Close/i.test(taskName) ? 'Clear to close check' : 'Financing and appraisal deadline');
      setText(messageFor(taskName, first, dealAddress));
    }
    setOpen(mode);
  };

  return (
    <div style={{ margin: '6px 0' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, color: '#1A1A1A', fontWeight: 600 }}>Lender: {name || 'No lender saved'}{company ? ` (${company})` : ''}</span>
        <a href={digits ? `tel:${digits}` : undefined} style={digits ? btn : btnOff} onClick={() => logContact(task.transactionId, { kind: 'call', toPhone: digits, role: 'Lender', taskName: task.name || task.title || '' })}>Call</a>
        <CallTalkingPoints task={task} />
        <button type="button" onClick={() => toggle('text')} disabled={!digits} style={digits ? btn : btnOff}>Text</button>
        <button type="button" onClick={() => toggle('email')} disabled={!email} style={email ? btn : btnOff}>Email</button>
        {digits && <span style={{ fontSize: 13, color: '#5B6270' }}>{lender.phone}</span>}
        {email && <span style={{ fontSize: 13, color: '#5B6270' }}>{email}</span>}
      </div>
      {open === 'text' && digits && (
        <div style={{ marginTop: 8, padding: 10, border: '1px solid #D5D9E0', borderRadius: 8, background: '#FFFFFF' }}>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} style={{ width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 6, border: '1px solid #D5D9E0', fontSize: 14, fontFamily: 'inherit', color: '#1A1A1A' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <a href={`sms:${digits}?body=${encodeURIComponent(text)}`} style={btn} onClick={() => onSent && onSent()}>Send</a>
            <button type="button" onClick={() => setOpen(null)} style={btnCancel}>Cancel</button>
          </div>
        </div>
      )}
      {open === 'email' && email && (
        <div style={{ marginTop: 8, padding: 10, border: '1px solid #D5D9E0', borderRadius: 8, background: '#FFFFFF' }}>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 6, border: '1px solid #D5D9E0', fontSize: 14, marginBottom: 8 }} />
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} style={{ width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 6, border: '1px solid #D5D9E0', fontSize: 14, fontFamily: 'inherit', color: '#1A1A1A' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <a href={`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`} style={btn} onClick={() => onSent && onSent()}>Send</a>
            <button type="button" onClick={() => setOpen(null)} style={btnCancel}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
